import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
// Registers the String.prototype.dictReplace extension the remove URL uses (main.ts does this in the app).
import '../../../_services/string-extensions';

/**
 * One shared fiskaly TEST organisation that demo companies' locations are placed under (#933), as
 * `GET /Management/Fiskaly/ListDemoOrganizations` answers it. Never carries the secret.
 */
export interface DemoOrganization {
  /** Our row id, used to remove the entry. */
  id: string;
  /** fiskaly's organisation id. */
  organizationId: string;
  /** The operator's name for it. */
  label: string;
  /** The TEST api key; shown so the row can be matched in the dashboard. Useless without the secret. */
  apiKey: string;
  /** How many TSS it may hold (fiskaly's TEST limit, five, unless set otherwise). */
  capacity: number;
  /** Slots demo locations currently take. */
  used: number;
  /** `capacity - used`, never negative. */
  free: number;
}

/** What the operator enters to add a demo organisation. */
export interface DemoOrganizationEntry {
  organizationId: string;
  label: string;
  apiKey: string;
  apiSecret: string;
}

/**
 * The demo organisation pool on Owner → **Integrationen** (#933).
 *
 * A separate service from {@link FiskalyHttpService} so the Integrationen screen's own fiskaly stubs stay as
 * they are: the pool is its own card with its own lifecycle. See
 * `docs/articles/platform/fiskaly-managing-organization.md`, section „Demo organisations".
 */
@Injectable({
  providedIn: 'root'
})
export class FiskalyDemoOrganizationHttpService {
  constructor(@Inject('BASE_URL') private baseUrl: string, private httpClient: HttpClient) {
  }

  list(): Observable<DemoOrganization[]> {
    return this.httpClient.get<DemoOrganization[]>(
      `${this.baseUrl}${ServerEndpoints.Management_Fiskaly_ListDemoOrganizations}`);
  }

  /**
   * Adds an organisation. The server stores it only after fiskaly says its key is TEST and belongs to this
   * organisation, and the organisation has no LIVE enabled; otherwise it answers a catalogued `E_LEGAL_DEMO_*`.
   */
  add(entry: DemoOrganizationEntry): Observable<DemoOrganization> {
    return this.httpClient.post<DemoOrganization>(
      `${this.baseUrl}${ServerEndpoints.Management_Fiskaly_AddDemoOrganization}`, entry);
  }

  /** Removes our record of an organisation that holds no demo. The organisation stays at fiskaly. */
  remove(id: string): Observable<string> {
    return this.httpClient.delete<string>(
      `${this.baseUrl}${ServerEndpoints.Management_Fiskaly_RemoveDemoOrganization_$id$.dictReplace({id})}`);
  }
}
